import { MULTIVARIATE_EXPERIMENT_CONFIG } from '../config/multivariateExperimentConfig';

export interface ParsedExperimentVariants {
  home: string;
  // goldCoinPlp: string;
  plp: string;
  pdp: string;
  rawCookies: Record<string, string>;
}

/**
 * Checks if tags string indicates a gold coin, gold bar, or bullion page.
 */
export function isCoinOrBarPageByTags(tags?: string | null): boolean {
  if (!tags || typeof tags !== 'string') return false;
  const lower = tags.toLowerCase();
  return lower.includes('coin') || lower.includes('bar') || lower.includes('bullion');
}

/**
 * Checks if a PLP URL path after `/jewellery/` contains "coin", "bar", or "bullion".
 */
export function isCoinOrBarPageByUrl(pathname?: string | null): boolean {
  if (!pathname || typeof pathname !== 'string') return false;
  const lower = pathname.toLowerCase();
  const jewelleryIdx = lower.indexOf('/jewellery/');
  if (jewelleryIdx === -1) return false;
  const afterJewellery = lower.substring(jewelleryIdx + '/jewellery/'.length);
  return afterJewellery.includes('coin') || afterJewellery.includes('bar') || afterJewellery.includes('bullion');
}

/**
 * Helper to test if PLP is a Gold Coin page by tags or URL.
 */
export function isGoldCoinPlpPage(tags?: string | null, urlPath?: string | null): boolean {
  return isCoinOrBarPageByTags(tags) || isCoinOrBarPageByUrl(urlPath);
}

/**
 * Formats/normalizes raw variant string (e.g. 'v2' -> 'V2').
 * Returns 'NONE' if empty or invalid.
 */
export function normalizeVariant(rawVal?: string | null): string {
  if (!rawVal || typeof rawVal !== 'string') return 'NONE';
  const trimmed = rawVal.trim().toUpperCase();
  if (!trimmed) return 'NONE';
  if (/^V[0-9]+$/.test(trimmed)) {
    return trimmed;
  }
  return trimmed;
}

/**
 * Helper to get variant for a specific cookie prefix following exact numerical index matching.
 * - Extracts integer from cookie name (e.g. "EX_HOME_2" -> 2)
 * - Returns highest index value (upper-cased, or '' if empty)
 * - Returns 'NONE' if no matching indexed cookie exists
 */
export function getVariantForPrefixFromCookieString(rawCookies: string | null | undefined, prefix: string): string {
  if (!rawCookies || typeof rawCookies !== 'string' || rawCookies.trim().length === 0) {
    return 'NONE';
  }
  const cookies = rawCookies.split(';');
  let highestIndex = -1;
  let highestValue = '';
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(prefix)) {
      const eqIndex = cookie.indexOf('=');
      if (eqIndex !== -1) {
        const cookieName = cookie.substring(0, eqIndex).trim();
        const value = cookie.substring(eqIndex + 1).trim();
        const indexNum = parseInt(cookieName.substring(prefix.length), 10);
        if (!isNaN(indexNum) && indexNum > highestIndex) {
          highestIndex = indexNum;
          highestValue = value.toUpperCase();
        }
      }
    }
  }
  return highestIndex === -1 ? 'NONE' : highestValue;
}

/**
 * Parses cookie string (from req.headers.cookie or document.cookie)
 * and extracts variant values for all defined experiment cookie prefixes.
 */
export function parseExperimentCookies(rawCookies?: string | null): ParsedExperimentVariants {
  const result: ParsedExperimentVariants = {
    home: getVariantForPrefixFromCookieString(rawCookies, MULTIVARIATE_EXPERIMENT_CONFIG.HOME.prefix),
    // goldCoinPlp: getVariantForPrefixFromCookieString(rawCookies, MULTIVARIATE_EXPERIMENT_CONFIG.GOLD_COIN_PLP.prefix),
    plp: getVariantForPrefixFromCookieString(rawCookies, MULTIVARIATE_EXPERIMENT_CONFIG.PLP.prefix),
    pdp: getVariantForPrefixFromCookieString(rawCookies, MULTIVARIATE_EXPERIMENT_CONFIG.PDP.prefix),
    rawCookies: {},
  };

  if (rawCookies && typeof rawCookies === 'string') {
    const parts = rawCookies.split(';');
    for (const part of parts) {
      const trimmed = part.trim();
      const eqIdx = trimmed.indexOf('=');
      if (eqIdx !== -1) {
        const name = trimmed.substring(0, eqIdx).trim();
        const value = trimmed.substring(eqIdx + 1).trim();
        if (
          name.startsWith(MULTIVARIATE_EXPERIMENT_CONFIG.HOME.prefix) ||
          // name.startsWith(MULTIVARIATE_EXPERIMENT_CONFIG.GOLD_COIN_PLP.prefix) ||
          name.startsWith(MULTIVARIATE_EXPERIMENT_CONFIG.PLP.prefix) ||
          name.startsWith(MULTIVARIATE_EXPERIMENT_CONFIG.PDP.prefix)
        ) {
          result.rawCookies[name] = value;
        }
      }
    }
  }

  return result;
}
